import { TCountryCode } from 'countries-list';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Map from './Map';
import { geometry, getCountryPolygons2 } from '../functions/getCountryPolygons2';
import { flatten } from 'lodash';
import InteractiveMap from './InteractiveMap';
import { post } from '../mocks/postsMocks/postsStore';

type selection = {
  countryCode: TCountryCode
  color: string
};
const ShadedMap: React.FC<{ selectedCountries: selection[], posts?: post[] }> = ({ selectedCountries, posts }) => {
  const [allMapData, setAllMapData] = useState<Array<{ id: TCountryCode, color: string, mapData: geometry }>>([]);

  const allPolygons = useMemo(() => allMapData.map(({ mapData, color }) => ({ region: mapData, color })), [allMapData]);

  const addCountryPolygons = useCallback((selection: selection) => {
    getCountryPolygons2(
      selection.countryCode,
      (mapData) => {
        setAllMapData(prev => [
          ...prev,
          { mapData, id: selection.countryCode, color: selection.color }, // Keep track of the country
        ]);
      },
    );
  }, []);

  const removeCountryPolygons = useCallback((countryCode: TCountryCode) =>
    setAllMapData(prev => prev.filter(({ id }) => id !== countryCode)), [],
  );

  useEffect(() => {
    const newCountries = selectedCountries.filter(
      selection => !allMapData.find(data => data.id === selection.countryCode),
    );
    const removedCountries = allMapData.filter(
      data => !selectedCountries.find(selection => selection.countryCode === data.id),
    );
    newCountries.forEach(addCountryPolygons);
    removedCountries.forEach(({ id }) => removeCountryPolygons(id)); // this is causing the same fetch to keep looping infinitely
  }, [selectedCountries, addCountryPolygons, allMapData, removeCountryPolygons]);

  const shadedRegions = allPolygons.map(({ color, region }) => (
    {
      region: flatten(region).map(v1 =>
        v1.map(coordinates => ({
          lat: coordinates[1],
          lng: coordinates[0],
        })),
      ),
      color,
    }
  ));

  return (
    posts
      ? <InteractiveMap shadedRegions={shadedRegions} posts={posts} />
      : <Map shadedRegions={shadedRegions} />
  );
};

export default ShadedMap;
